
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Grid3x3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useProfileViews } from "@/hooks/useProfileViews";
import { profileRouterService } from "@/services/profileRouterService";
import { discoveryService, type FilterState } from "@/services/discoveryService";
import { useDiscoveryFilters } from "@/hooks/useDiscoveryFilters";
import AdPanel from "@/components/ads/AdPanel";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import DiscoveryFilters from "@/components/discovery/DiscoveryFilters";

const DiscoveryNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { handleProfileView } = useProfileViews();
  const { 
    showFilters, 
    setShowFilters, 
    filters, 
    setFilters, 
    hasActiveFilters 
  } = useDiscoveryFilters();

  const { data: newUsers, isLoading, refetch } = useQuery({
    queryKey: ['discovery-new-users-all', user?.id, filters],
    queryFn: () => discoveryService.fetchNewUsers(user?.id || '', 50, filters),
    enabled: !!user
  });

  const handleProfileViewAndNavigate = (userId: string, userProfile?: any) => {
    handleProfileView(userId);
    const profileRoute = profileRouterService.getProfileRoute(
      userId, 
      userProfile?.username || userProfile?.first_name, 
      userProfile?.display_name
    );
    navigate(profileRoute);
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <div className="min-h-screen bg-carbon text-ice-white">
      <AdPanel />
      <RightSideMenu />
      <div className="container mx-auto px-4 py-4 max-w-6xl">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/discovery')}
              className="text-ice-white hover:text-neon-red p-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-ice-white">New Users</h1>
              <p className="text-ice-white/60 text-sm">Latest members to join the community</p>
            </div>
          </div>
          
          <div className="relative">
            <Button 
              onClick={() => setShowFilters(true)}
              className="bg-carbon/80 hover:bg-carbon border border-ice-white/30 text-ice-white px-4 py-2 text-sm backdrop-blur-sm"
            >
              <Grid3x3 className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-neon-red rounded-full animate-pulse border-2 border-ice-white" />
              )}
            </Button>
          </div>
        </div>

        {/* Filters Modal */}
        {showFilters && (
          <DiscoveryFilters
            filters={filters}
            onFiltersChange={setFilters}
            onClose={() => setShowFilters(false)}
          />
        )}

        {isLoading ? (
          <div className="grid grid-cols-3 gap-0 w-full">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="w-full aspect-square bg-gray-900/50 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-0 w-full">
            {newUsers?.map((profile) => (
              <div
                key={profile.user_id}
                className="relative w-full aspect-square rounded-lg overflow-hidden cursor-pointer group bg-gray-900/50"
                onClick={() => handleProfileViewAndNavigate(profile.user_id, profile)}
              >
                {profile.photo_urls?.[0] ? (
                  <img 
                    src={profile.photo_urls[0]} 
                    alt={profile.display_name} 
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                    <div className="text-2xl font-bold text-gray-600">
                      {profile.display_name?.charAt(0) || '?'}
                    </div>
                  </div>
                )}
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                
                {/* Online indicator */}
                {profile.isOnline && (
                  <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-toxic-green shadow-lg border-2 border-white" />
                )}
                
                {/* Profile info overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-2 text-white">
                  <h3 className="font-semibold text-xs leading-tight mb-1">
                    {profile.display_name || 'Unknown'}
                  </h3>
                  
                  {profile.location && (
                    <div className="flex items-center text-xs text-gray-300 mb-1">
                      <span className="truncate">{profile.location}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    {profile.age && <span>{profile.age}y</span>}
                    {profile.distance && (
                      <span className="bg-black/50 px-1 py-0.5 rounded text-xs">
                        {profile.distance}km
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(!newUsers || newUsers.length === 0) && !isLoading && (
              <div className="col-span-3 text-center text-ice-white/50 text-sm py-8">
                No profiles found. <button onClick={handleRefresh} className="text-neon-red hover:underline">Refresh</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoveryNew;
